<template>
  <LegacySettingsContainer
    :enableMainAction="enableSave"
    @cancel="handleCancel"
    @save="handleSave"
  >
    <button
      :class="$style.actionLink"
      @click.prevent="handleCancel"
    >
      <svg viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg" :class="$style.backArrow" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M6.429 12.914l1.174-1.105-4.148-4.413 9.936-.013V5.77l-9.893.013 4.42-4.155L6.813.453.391 6.49l6.038 6.424z" fill="#626B73"/></svg>
      Back
    </button>
    <h4 :class="$style.header">
      Conditional Policies Settings
    </h4>
    <CollapsableSection
      :collapse="false"
      title="Device Certificates"
    >
      <CertificateSettings
        includeKbLink
      />
    </CollapsableSection>
    <CollapsableSection
      :collapse="false"
      title="Global Policy Settings"
      :unsetOverflow="true"
    >
      <p>
        For each resource that has an access policy, determine how users access the resource when
        no policies apply. Custom policies override the Global Policy. Users who aren’t enrolled
        in MFA will be prompted to enroll when a policy requires MFA, overriding any enrollment
        periods.
        <LinkExternal :href="$options.kbLink">
          Learn more
        </LinkExternal>
      </p>

      <p><strong>User Portal</strong></p>
      <GlobalPolicySettingsPicker
        stateName="UserPortalFallbackPolicyModel"
      />

      <p><strong>SSO Applications</strong> (with a valid user portal session)</p>
      <GlobalPolicySettingsPicker
        stateName="SsoApplicationAccessPolicyModel"
      />

      <div>
        <p><strong>JumpCloud LDAP</strong></p>
        <p>
          To use the LDAP Global Policy,
          ensure you target your LDAP applications to the following URL:
          <strong>{{ getLdapUrl() }}</strong>
        </p>

        <GlobalPolicySettingsPicker
          stateName="LdapAccessPolicyModel"
        />

        <div
          v-if="shouldShowMFALDAPWarning"
          data-test-id="MFALDAPWarningContainer"
        >
          <div :class="$style.mfaLDAPWarningActionsContainer">
            <Button
              dataTestId="enablePushButton"
              :isDisabled="userPortalPushConfig.enabled"
              :text="enablePushButtonText"
              @click="setEnablePushMFA(true)"
            />
            <Button
              v-if="userPortalPushConfig.enabled"
              :class="$style.undoButton"
              dataTestId="disablePushButton"
              :hasNoBackground="true"
              :outline="true"
              text="undo"
              type="secondary"
              @click="setEnablePushMFA(false)"
            />
          </div>

          <div :class="$style.mfaLDAPWarningFooter">
            <div :class="$style.mfaLDAPWarningFooterMessageBefore">
              <strong>*</strong>
            </div>
            <p>
              <strong>You must enable JumpCloud Protect on your organization.</strong>
              JumpCloud LDAP applications only support JumpCloud Protect Push MFA.
              <br>
              Enabling JumpCloud Protect will enable it for the JumpCloud User Portal
              as well as any SSO applications that MFA is enforced on.
            </p>
          </div>
        </div>
      </div>
    </CollapsableSection>
  </LegacySettingsContainer>
</template>

<script>
// Framework
import { mapActions, mapGetters, mapState } from 'vuex';

// Constants
import {
  AuthnPolicyConsts, ItemTypeConsts, ItemTypeRoutes, StoreModelMap,
} from 'client/util/Constants';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';

// Components
import { Button } from '@jumpcloud/ui-components';
import CertificateSettings from 'client/Security/CertificateSettings';
import CollapsableSection from 'components/ContentSection/CollapsableSection.vue';
import GlobalPolicySettingsPicker from 'client/Security/GlobalPolicySettingsPicker.vue';
import LegacySettingsContainer from 'client/Home/components/LegacySettingsContainer.vue';
import LinkExternal from 'client/components/Links/LinkExternal.vue';
import RouteGuardMixin from 'client/AdminPortal/router/guards/RouteGuardMixin';

// Utils
import { getLdapUrl, simplePolicy } from 'client/Security/utils/AuthnPolicyUtilities';
import Util from 'client/util/Util';

const { responseMessages } = AuthnPolicyConsts;

const orgModel = StoreModelMap[ItemTypeConsts.organizations];
const legacy = 'legacy';

export default {
  name: 'AuthNPoliciesSettings',

  components: {
    Button,
    CertificateSettings,
    CollapsableSection,
    GlobalPolicySettingsPicker,
    LinkExternal,
    LegacySettingsContainer,
  },

  mixins: [RouteGuardMixin],

  authNPoliciesSettingsVueRoute: ItemTypeRoutes[ItemTypeConsts.authNPoliciesSettings].replace('#', ''),

  data() {
    return {
      shouldShowMFALDAPWarning: false,
      allowSubRoutesNavigation: this.$options.authNPoliciesSettingsVueRoute,
    };
  },

  legacy,
  Util,

  kbLink: KbArticleUrls.aboutTheGlobalPolicySetting,

  computed: {
    ...mapState('PushMfaUserPortalModule', {
      userPortalPushConfig: 'config',
    }),

    ...mapGetters(orgModel, {
      hasOrgModelChange: 'hasItemChanged',
    }),

    ...mapGetters('UserPortalFallbackPolicyModel', {
      fallbackUserPortalChange: 'hasItemChanged',
    }),

    ...mapGetters('SsoApplicationAccessPolicyModel', {
      fallbackAppChange: 'hasItemChanged',
    }),

    ...mapGetters('LdapAccessPolicyModel', {
      fallbackLdapChange: 'hasItemChanged',
      fallbackLdapItem: 'item',
    }),

    enablePushButtonText() {
      return this.userPortalPushConfig.enabled
        ? 'JumpCloud Protect Enabled!'
        : 'Enable JumpCloud Protect';
    },

    enableSave() {
      return this.hasOrgModelChange
        || this.fallbackUserPortalChange
        || this.fallbackAppChange
        || this.shouldAllowLdapPolicySave;
    },

    shouldAllowLdapPolicySave() {
      // Allow save when:
      // Policy selection has changed
      // AND
      // If Push is enabled --> always allow
      // If Push not enabled --> push warning is not rendered
      return this.fallbackLdapChange
        && (
          this.userPortalPushConfig.enabled
          || !this.shouldShowMFALDAPWarning
        );
    },
  },

  watch: {
    fallbackLdapItem: {
      handler(fallbackLdapItem) {
        this.setMFALDAPWarning(fallbackLdapItem, this.userPortalPushConfig);
      },
      deep: true,
    },
  },

  beforeMount() {
    this.initializePushModule();

    this.setMFALDAPWarning(this.fallbackLdapItem, this.userPortalPushConfig);
  },

  beforeDestroy() {
    this.resetOrgModel();
  },

  methods: {
    ...mapActions('PushMfaModule', {
      initializePushModule: 'initialize',
    }),

    ...mapActions('PushMfaUserPortalModule', {
      changeUserPortalPushConfig: 'changeConfig',
      saveUserPortalPushConfig: 'saveChanges',
    }),

    ...mapActions(orgModel, {
      resetOrgModel: 'resetItem',
      saveOrgModel: 'saveItem',
    }),

    ...mapActions('AlertModule', [
      'triggerErrorAlert',
      'triggerSuccessAlert',
    ]),

    ...mapActions('UserPortalFallbackPolicyModel', {
      saveUserPortalFallbackPolicy: 'saveItem',
    }),

    ...mapActions('SsoApplicationAccessPolicyModel', {
      saveAppFallbackPolicy: 'saveItem',
    }),

    ...mapActions('LdapAccessPolicyModel', {
      saveLdapFallbackPolicy: 'saveItem',
    }),

    getLdapUrl,

    handleCancel() {
      this.$options.Util.redirect(ItemTypeRoutes[ItemTypeConsts.authNPolicies]);
      // We're calling resetOrgModel in beforeDestroy to keep the UnsavedChanges Modal working
    },

    handleSave() {
      const policyChanges = [
        { key: 'org', hasChange: this.hasOrgModelChange, action: this.saveOrgModelSettings },
        { key: 'userportal', hasChange: this.fallbackUserPortalChange, action: this.saveUserPortalFallbackPolicy },
        { key: 'sso', hasChange: this.fallbackAppChange, action: this.saveAppFallbackPolicy },
        { key: 'ldap', hasChange: this.fallbackLdapChange, action: this.saveLdapFallbackPolicy },
      ];

      return Promise.all(policyChanges.map(policy => this.savePolicy(policy)));
    },

    // This method signals the router that
    // a confirmation modal needs to be shown before leaving the page.
    hasModelChanged() {
      return this.enableSave;
    },

    async saveOrgModelSettings() {
      await this.saveOrgModel();
      // This should be happening as a part of the save but it isn't for some reason
      this.resetOrgModel();
      this.$options.Util.redirect(ItemTypeRoutes[ItemTypeConsts.authNPolicies]);
    },

    async savePolicy(policy) {
      if (policy.hasChange) {
        const { success, failure } = responseMessages[policy.key];
        try {
          await policy.action();
          this.triggerSuccessAlert(success);
        } catch {
          this.triggerErrorAlert(failure);
        }
      }
    },

    setEnablePushMFA(isEnabled = false) {
      this.changeUserPortalPushConfig({
        ...this.userPortalPushConfig,
        enabled: isEnabled,
      });

      this.saveUserPortalPushConfig();
    },

    setMFALDAPWarning(fallbackLdapItem, userPortalPushConfig) {
      const policy = simplePolicy(fallbackLdapItem);

      this.shouldShowMFALDAPWarning = (
        !userPortalPushConfig.enabled && policy.isMFARequired
      );
    },
  },
};
</script>

<style module>
@value jcGrey from '~css/brand-colors.css';

.backArrow {
  width: 13px;
}

.actionLink,
.actionLink:hover {
  background-color: transparent;
  color: jcGrey;
  padding: 0;
}

.header.header {
  font-weight: bold;
  margin: 20px 15px;
}

.mfaLDAPWarningActionsContainer {
  display: flex;
  flex-direction: row;
}

.undoButton {
  margin-left: 1.5em;
}

.mfaLDAPWarningFooter {
  display: flex;
  margin-top: 1em;
}

.mfaLDAPWarningFooterMessageBefore {
  flex: none;
  margin-left: 0.5em;
  margin-right: 0.5em;
}
</style>
